/**
 * PathNode - Flat ribbon/strip geometry along waypoints
 *
 * Creates a road or river as a flat strip mesh following a smooth path.
 * Unlike TubeNode (round cross-section), PathNode creates a flat ribbon
 * on the ground plane — ideal for roads, rivers, paths.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';

const pathEndpoints = {
  ...internals({
    geometryOut: { dataType: 'three:geometry', isOutput: true },
  }),
  ...params({
    points: {
      dataType: 'array',
      value: [[-20, 0], [0, 10], [20, 0]],
      label: 'Waypoints (X,Z)',
    },
    width: { value: 2, min: 0.1, max: 20, step: 0.1 },
    segments: { value: 32, min: 4, max: 128, step: 1, label: 'Path Segments' },
    elevation: { value: 0.05, min: 0, max: 5, step: 0.01, label: 'Y Offset' },
    smooth: { dataType: 'boolean', value: true },
  }),
};

class PathNode extends BaseNode<typeof pathEndpoints> {
  private three: Three;
  private mesh: any = null;
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  buildGeometry(): any {
    const rawPoints = this.params.points.value;
    if (!Array.isArray(rawPoints) || rawPoints.length < 2) return null;

    const elevation = this.params.elevation.value;
    const halfWidth = this.params.width.value / 2;
    const numSegments = this.params.segments.value;
    const useSmooth = this.params.smooth.value;

    // Parse waypoints as [x, z] pairs → Vector3 at y = elevation
    const waypoints = rawPoints
      .filter((p: any) => Array.isArray(p) && p.length >= 2)
      .map((p: any) => new this.three.Vector3(Number(p[0]), elevation, Number(p[1])));

    if (waypoints.length < 2) return null;

    // Create curve through waypoints
    let samplePoints: any[];

    if (useSmooth && waypoints.length >= 2) {
      const curve = new this.three.CatmullRomCurve3(waypoints, false);
      samplePoints = curve.getPoints(numSegments);
    } else {
      // Linear interpolation between waypoints
      samplePoints = [];
      const totalDist = waypoints.reduce((sum: number, p: any, i: number) =>
        i === 0 ? 0 : sum + p.distanceTo(waypoints[i - 1]), 0);

      for (let i = 0; i <= numSegments; i++) {
        const t = i / numSegments;
        const targetDist = t * totalDist;

        let accumulated = 0;
        for (let j = 1; j < waypoints.length; j++) {
          const segDist = waypoints[j].distanceTo(waypoints[j - 1]);
          if (accumulated + segDist >= targetDist || j === waypoints.length - 1) {
            const localT = segDist > 0 ? (targetDist - accumulated) / segDist : 0;
            samplePoints.push(
              new this.three.Vector3().lerpVectors(waypoints[j - 1], waypoints[j], Math.min(1, localT))
            );
            break;
          }
          accumulated += segDist;
        }
      }
    }

    if (samplePoints.length < 2) return null;

    // Build triangle strip: for each sample, compute perpendicular in XZ plane
    const vertCount = samplePoints.length * 2;
    const positions = new Float32Array(vertCount * 3);
    const normals = new Float32Array(vertCount * 3);
    const uvs = new Float32Array(vertCount * 2);

    for (let i = 0; i < samplePoints.length; i++) {
      const curr = samplePoints[i];

      // Compute forward direction in XZ
      let dx: number, dz: number;
      if (i < samplePoints.length - 1) {
        dx = samplePoints[i + 1].x - curr.x;
        dz = samplePoints[i + 1].z - curr.z;
      } else {
        dx = curr.x - samplePoints[i - 1].x;
        dz = curr.z - samplePoints[i - 1].z;
      }

      // Perpendicular in XZ plane (rotate 90 degrees)
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const px = -dz / len;
      const pz = dx / len;

      const vi = i * 2;

      // Left vertex
      positions[(vi) * 3] = curr.x + px * halfWidth;
      positions[(vi) * 3 + 1] = curr.y;
      positions[(vi) * 3 + 2] = curr.z + pz * halfWidth;

      // Right vertex
      positions[(vi + 1) * 3] = curr.x - px * halfWidth;
      positions[(vi + 1) * 3 + 1] = curr.y;
      positions[(vi + 1) * 3 + 2] = curr.z - pz * halfWidth;

      // Normals: straight up
      normals[(vi) * 3 + 1] = 1;
      normals[(vi + 1) * 3 + 1] = 1;

      // UVs
      const t = i / (samplePoints.length - 1);
      uvs[vi * 2] = 0;
      uvs[vi * 2 + 1] = t;
      uvs[(vi + 1) * 2] = 1;
      uvs[(vi + 1) * 2 + 1] = t;
    }

    // Build index buffer for triangle strip
    const triCount = (samplePoints.length - 1) * 2;
    const indices = new Uint32Array(triCount * 3);
    let idx = 0;

    for (let i = 0; i < samplePoints.length - 1; i++) {
      const v0 = i * 2;
      const v1 = i * 2 + 1;
      const v2 = (i + 1) * 2;
      const v3 = (i + 1) * 2 + 1;

      // Triangle 1
      indices[idx++] = v0;
      indices[idx++] = v2;
      indices[idx++] = v1;
      // Triangle 2
      indices[idx++] = v1;
      indices[idx++] = v2;
      indices[idx++] = v3;
    }

    const geometry = new this.three.BufferGeometry();
    geometry.setIndex(new this.three.BufferAttribute(indices, 1));
    geometry.setAttribute('position', new this.three.BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new this.three.BufferAttribute(normals, 3));
    geometry.setAttribute('uv', new this.three.BufferAttribute(uvs, 2));

    return geometry;
  }

  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  animateCallback = () => {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  };

  onParamChanged(): void {
    if (this.rebuildCallback) {
      this.rebuildCallback();
    } else if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }
}

export const pathNodeDef: NodeDef<PathNode> = {
  type: 'Path',
  shortName: 'path',
  info: 'Flat ribbon/strip geometry along waypoints. Ideal for roads, rivers, and paths.',
  category: 'architecture',
  endpoints: pathEndpoints,
  ctor: PathNode,
};
